import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, delay } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  getJobError,
  getJobsList,
  getJobsListResponse,
  loadMoreJobsList,
  loadMoreJobsListResponse,
  removeJob,
  removeJobError,
  removeJobItem,
  removeJobResponse,
  saveJob,
  saveJobError,
  saveJobResponse,
  searchJobsList,
  searchJobsListResponse,
  updateJob,
  updateJobError,
  updateJobResponse,
  getJobsListError,
} from './jobs.actions';
import { JobsResource } from '../resources/jobs.resource';
import { SLIDER_CLOSE_ANIMATION_DURATION } from '../../constants/animation-constants';
import { NotifyService } from '../../common/services/notify.service';

@Injectable()
export class JobsEffects {
  @Effect()
  getJobs$ = this.actions$.pipe(
    ofType(getJobsList),
    switchMap(({ params }) =>
      this.jobsResource.query(params).pipe(
        map((jobs) => getJobsListResponse({ jobs })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getJobError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  searchJobs$ = this.actions$.pipe(
    ofType(searchJobsList),
    switchMap(({ params }) =>
      this.jobsResource.search(params).pipe(
        map((jobs) => searchJobsListResponse({ jobs })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getJobsListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  loadMoreJobs$ = this.actions$.pipe(
    ofType(loadMoreJobsList),
    switchMap(({ params }) =>
      (params.q ? this.jobsResource.search(params) : this.jobsResource.query(params)).pipe(
        map((jobs) => loadMoreJobsListResponse({ jobs })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getJobsListError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  saveJob$ = this.actions$.pipe(
    ofType(saveJob),
    switchMap(({ job }) =>
      this.jobsResource.save(job, { include: 'package,cluster' } as any).pipe(
        map((jobResponse) => {
          const message = this.translate.instant('job.notifications.submitted.message', {
            id: jobResponse.id,
            package: jobResponse.package.name,
            cluster: jobResponse.cluster.name,
            creator: jobResponse.creator.display_name,
          });

          this.notify.info(message);

          return saveJobResponse({ data: jobResponse });
        }),
        catchError((response) => of(saveJobError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  updateJob$ = this.actions$.pipe(
    ofType(updateJob),
    switchMap(({ job, params, jobId }) =>
      this.jobsResource.update(jobId, job, { ...params, include: 'packages' } as any).pipe(
        map((jobResponse) => updateJobResponse({ data: jobResponse })),
        catchError((response) => of(updateJobError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removeJob$ = this.actions$.pipe(
    ofType(removeJob),
    switchMap(({ jobId }) =>
      this.jobsResource.remove(jobId).pipe(
        map((jobResponse) => removeJobResponse({ data: jobResponse })),
        catchError((response) => of(removeJobError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removeJobResponse$ = this.actions$.pipe(
    ofType(removeJobItem),
    delay(SLIDER_CLOSE_ANIMATION_DURATION),
    map(({ data }) => removeJobResponse({ data })),
  );

  constructor(
    private actions$: Actions,
    private jobsResource: JobsResource,
    private translate: TranslateService,
    private notify: NotifyService,
  ) {}
}
